const STORAGE_KEY = "@mendicino/api-cache";

interface CacheEntry {
  data: unknown;
  timestamp: number;
  ttl: number;
}

interface PersistedData {
  entries: [string, { data: unknown; timestamp: number; ttl: number }][];
  lruKeys: string[];
}

export class CacheService {
  private static instance: CacheService;
  private cache = new Map<string, CacheEntry>();
  private lruKeys: string[] = [];
  private readonly maxSize = 100;

  private constructor() {
    this.restoreFromStorage();
  }

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
      if (typeof window !== 'undefined') {
        (window as any).__invalidateApiCache = () => {
          CacheService.instance.invalidateAll();
          console.log('[Cache] Manual invalidation via window.__invalidateApiCache()');
        };
      }
    }
    return CacheService.instance;
  }

  get<T>(key: string): T | undefined {
    const entry = this.cache.get(key);
    if (!entry) {
      console.log(`[Cache] MISS ${key}`);
      return undefined;
    }

    const now = Date.now();
    const elapsed = (now - entry.timestamp) / 1000;
    if (elapsed > entry.ttl) {
      this.cache.delete(key);
      this.removeLruKey(key);
      this.persist();
      console.log(`[Cache] EXPIRED ${key}`);
      return undefined;
    }

    this.touchLruKey(key);
    this.persist();
    console.log(`[Cache] HIT ${key}`);
    return entry.data as T;
  }

  set<T>(key: string, data: T, ttl: number): void {
    this.ensureCapacity();
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
    this.touchLruKey(key);
    this.persist();
    console.log(`[Cache] SET ${key} (TTL: ${ttl}s)`);
  }

  invalidate(pattern?: string): void {
    if (!pattern) {
      this.invalidateAll();
      return;
    }
    for (const key of Array.from(this.cache.keys())) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.removeLruKey(key);
      }
    }
    this.persist();
    console.log(`[Cache] INVALIDATED pattern: ${pattern}`);
  }

  invalidateAll(): void {
    this.cache.clear();
    this.lruKeys = [];
    this.persist();
    console.log(`[Cache] INVALIDATED ALL`);
  }

  private persist(): void {
    try {
      const data: PersistedData = {
        entries: Array.from(this.cache.entries()),
        lruKeys: this.lruKeys,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // sessionStorage cheio ou indisponível — ignora
    }
  }

  private restoreFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data: PersistedData = JSON.parse(raw);
      this.cache = new Map(data.entries);
      this.lruKeys = data.lruKeys;

      // Remove entradas expiradas na restauração
      const now = Date.now();
      this.cache.forEach((entry, key) => {
        if ((now - entry.timestamp) / 1000 > entry.ttl) {
          this.cache.delete(key);
          this.removeLruKey(key);
        }
      });
      if (this.cache.size > 0) {
        console.log(`[Cache] RESTORED ${this.cache.size} entries from sessionStorage`);
      }
    } catch {
      // Dado corrompido ou indisponível — começa limpo
    }
  }

  private touchLruKey(key: string): void {
    this.removeLruKey(key);
    this.lruKeys.push(key);
  }

  private removeLruKey(key: string): void {
    const idx = this.lruKeys.indexOf(key);
    if (idx !== -1) {
      this.lruKeys.splice(idx, 1);
    }
  }

  private ensureCapacity(): void {
    while (this.cache.size >= this.maxSize) {
      const oldest = this.lruKeys.shift();
      if (oldest) {
        this.cache.delete(oldest);
        console.log(`[Cache] EVICTED ${oldest} (LRU)`);
      } else {
        break;
      }
    }
  }
}
